/* TreatmentDashboard — redesigned post-fitment dashboard.
 *
 * iter93 — the doctor's primary status surface for an active case. Replaces
 * the older single-column stat layout with a 5-section structure that
 * mirrors the doctor's actual mental model:
 *
 *   1. Flags & alerts (top, amber) — anything needing action
 *   2. Aligner progress (4 metric cards + tier allowance) — the core status
 *   3. Quick actions (6-button strip) — what the doctor or staff can do
 *   4. Progress photos (5-week strip + Compare) — visual context
 *   5. Visit log + Compliance (side-by-side, supporting context)
 *
 * Data sources are all real fields on the case row + the existing
 * /child-allowance + /patient-photos endpoints. No new data models added.
 *
 * Mounts inside TreatmentTab.jsx — only rendered when status >= treatment_started.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
    Tooth, CheckCircle, Plus, Minus, Calendar as CalendarIcon, Sparkle,
    WarningCircle, FilePdf, Image as ImageIcon, X, Flag, ArrowsCounterClockwise,
    Crown, Camera, ClipboardText, Heart, ArrowsLeftRight,
} from "@phosphor-icons/react";
import MarkFittedDialog from "@/components/case/MarkFittedDialog";
import CareCardDialog from "@/components/case/CareCardDialog";


/* ─────────────────────────────────────────────────────────────────────── */
/* Utility — small pure helpers used throughout this file. */

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); }
    catch { return "—"; }
}
function fmtDateLong(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return "—"; }
}
function daysBetween(fromIso, toIso) {
    if (!fromIso) return null;
    const from = new Date(fromIso);
    const to = toIso ? new Date(toIso) : new Date();
    return Math.max(0, Math.floor((to.getTime() - from.getTime()) / 86400000));
}
function addDays(iso, days) {
    if (!iso) return null;
    const d = new Date(iso);
    d.setDate(d.getDate() + days);
    return d.toISOString();
}


export default function TreatmentDashboard({ caseData, onReload }) {
    const c = caseData;
    const navigate = useNavigate();
    const [advancing, setAdvancing] = useState(false);
    const [careCardOpen, setCareCardOpen] = useState(false);

    const stage = Number(c.current_aligner_stage || 0);
    const total = Number(c.total_aligners || c.aligner_count || 0);
    const cadence = Number(c.aligner_cadence_days || 7);
    const fittedAt = c.fitted_at || c.treatment_activated_at;
    const stageAnchor = c.aligner_stage_updated_at || fittedAt;
    const dayOfTray = stageAnchor ? daysBetween(stageAnchor) + 1 : null;
    const nextSwitchInDays = dayOfTray ? Math.max(0, cadence - dayOfTray + 1) : null;
    const pct = total > 0 ? Math.round((stage / total) * 100) : 0;
    const isFinal = total > 0 && stage >= total;
    const treatmentDays = daysBetween(fittedAt);

    /* iter95 — effective wear schedule: doctor's override beats planner's
       which beats the implicit cadence fallback. Used by the est-completion
       math below so per-aligner overrides actually move the date. */
    const plan = c.plan || {};
    const effectiveSchedule = useMemo(() => {
        const docDef = plan.doctor_wear_schedule_default_days;
        const docOv = plan.doctor_wear_schedule_overrides;
        if (docDef != null || (Array.isArray(docOv) && docOv.length > 0)) {
            return {
                default_days: Number(docDef ?? plan.wear_schedule_default_days ?? cadence),
                overrides: Array.isArray(docOv) ? docOv : [],
            };
        }
        const plnDef = plan.wear_schedule_default_days;
        const plnOv = plan.wear_schedule_overrides;
        if (plnDef != null || (Array.isArray(plnOv) && plnOv.length > 0)) {
            return {
                default_days: Number(plnDef ?? cadence),
                overrides: Array.isArray(plnOv) ? plnOv : [],
            };
        }
        return { default_days: cadence, overrides: [] };
    }, [plan.doctor_wear_schedule_default_days, plan.doctor_wear_schedule_overrides, plan.wear_schedule_default_days, plan.wear_schedule_overrides, cadence]);

    /* iter93.1 + iter95 — Estimated completion: forward-project from today's
       actual position using the EFFECTIVE wear schedule (per-stage cadence)
       instead of a single global cadence.
       Math:
         days_left_in_current = max(0, schedule(stage).days - day_of_tray)
         days_in_future_trays = Σ schedule(i).days for i in (stage+1..total)
         days_remaining       = sum
         estimated_completion = today + days_remaining
       Returns null when we can't compute (no fitted_at OR no total). */
    const completion = useMemo(() => {
        if (isFinal) return { label: "Final tray", sub: "0d to go", iso: null };
        if (!fittedAt || total <= 0 || !stageAnchor) return null;
        const defDays = Math.max(1, Number(effectiveSchedule.default_days || cadence));
        const ovMap = new Map();
        (effectiveSchedule.overrides || []).forEach((o) => {
            const s = Number(o.stage);
            const d = Number(o.days);
            if (s >= 1 && s <= total && d >= 1) ovMap.set(s, d);
        });
        const daysFor = (s) => (ovMap.has(s) ? ovMap.get(s) : defDays);
        const currentTrayDays = stage >= 1 ? daysFor(stage) : defDays;
        const daysLeftInCurrent = Math.max(0, currentTrayDays - (dayOfTray || 0));
        let daysInFuture = 0;
        for (let i = Math.max(1, stage) + 1; i <= total; i++) daysInFuture += daysFor(i);
        const daysRemaining = daysLeftInCurrent + daysInFuture;
        if (daysRemaining <= 0) return { label: "This week", sub: "Final stage", iso: null };
        const date = new Date();
        date.setDate(date.getDate() + daysRemaining);
        const iso = date.toISOString();
        const label = date.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
        let sub;
        if (daysRemaining < 60) {
            const weeks = Math.max(1, Math.round(daysRemaining / 7));
            sub = `~${weeks} week${weeks === 1 ? "" : "s"} left`;
        } else {
            const months = Math.max(1, Math.round(daysRemaining / 30));
            sub = `~${months} months left`;
        }
        return { label, sub, iso, daysRemaining };
    }, [isFinal, fittedAt, total, stageAnchor, cadence, dayOfTray, stage, effectiveSchedule]);

    /* ───── Patient photos — bucket into 5 most recent weeks. */
    const [photos, setPhotos] = useState(null);
    useEffect(() => {
        api.get(`/cases/${c.id}/patient-photos`)
            .then((r) => setPhotos(r.data || []))
            .catch(() => setPhotos([]));
    }, [c.id]);

    /* ───── Allowance for quick-action gating. We don't need the full ledger
       here (it's already mounted higher up in WorkflowTab); we just need the
       counts to disable buttons appropriately. */
    const [allowance, setAllowance] = useState({ refinement: null, retainer: null });
    useEffect(() => {
        if (!c.id) return;
        Promise.all([
            api.get(`/doctor/cases/${c.id}/child-allowance`, { params: { kind: "refinement" } })
                .then((r) => r.data).catch(() => null),
            api.get(`/doctor/cases/${c.id}/child-allowance`, { params: { kind: "retainer" } })
                .then((r) => r.data).catch(() => null),
        ]).then(([ref, ret]) => setAllowance({ refinement: ref, retainer: ret }));
    }, [c.id]);

    /* ───── Stage advance / step-back handler. */
    const setStage = async (next) => {
        const safe = Math.max(0, Math.min(total || 99, next));
        setAdvancing(true);
        try {
            await api.patch(`/cases/${c.id}/aligner-stage`, { current_aligner_stage: safe });
            toast.success(`Stage updated to ${safe}`);
            onReload?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't update stage");
        } finally {
            setAdvancing(false);
        }
    };

    /* ───── Spawn refinement / retainer child case. Mirrors LinkedCasesPanel
       logic — calls the spawn endpoint then navigates to the child wizard. */
    const [spawnBusy, setSpawnBusy] = useState(null);
    const spawnChild = async (kind) => {
        setSpawnBusy(kind);
        try {
            const r = await api.post(`/cases/${c.id}/${kind}`, {});
            const child = r.data;
            toast.success(`${kind === "refinement" ? "Refinement" : "Retainer"} draft ${child.code} created.`);
            navigate(`/doctor/cases/new?draft=${child.id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || `Couldn't create ${kind}`);
        } finally {
            setSpawnBusy(null);
        }
    };

    /* ───── Flag issue handler — placeholder until a dedicated flag endpoint
       lands. Today this navigates to the case Chat tab where the doctor
       can raise a concern (existing flow). */
    const onFlagIssue = () => {
        navigate(`/doctor/cases/${c.id}?tab=chat`);
        toast.info("Open a chat to raise a concern with the planner / lab.");
    };

    /* ───── Compute flags. Each flag is a stale data-derived alert that
       deserves the doctor's attention. */
    const flags = useMemo(() => {
        const out = [];
        // Photo upload gap: 4+ weeks of treatment + last photo > 14 days old.
        if (photos && photos.length > 0 && treatmentDays != null && treatmentDays >= 28) {
            const lastPhoto = photos
                .slice()
                .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at))[0];
            const lastUploadAgo = daysBetween(lastPhoto.uploaded_at);
            if (lastUploadAgo >= 14) {
                out.push({
                    id: "photo-gap",
                    tone: "amber",
                    title: `Patient hasn't uploaded photos in ${lastUploadAgo} days`,
                    desc: "Nudge them via WhatsApp — last photo: " + fmtDateLong(lastPhoto.uploaded_at),
                });
            }
        }
        if (photos && photos.length === 0 && treatmentDays != null && treatmentDays >= 21) {
            out.push({
                id: "no-photos",
                tone: "amber",
                title: "Patient has uploaded no progress photos yet",
                desc: "3+ weeks into treatment. Set the expectation with a quick nudge.",
            });
        }
        // Switch overdue: dayOfTray > cadence + 2 means they're stuck on this tray.
        if (dayOfTray != null && dayOfTray > cadence + 2 && !isFinal) {
            out.push({
                id: "switch-overdue",
                tone: "red",
                title: `Tray switch overdue by ${dayOfTray - cadence} days`,
                desc: `Expected switch was ${dayOfTray - cadence} day(s) ago. Advance the stage or document the delay.`,
            });
        }
        // Refinement allowance exhausted hint — only when the doctor might want one
        if (allowance.refinement && allowance.refinement.included > 0 && allowance.refinement.remaining === 0) {
            out.push({
                id: "refinement-limit",
                tone: "slate",
                title: "You've used all included refinements on this tier",
                desc: `${allowance.refinement.used}/${allowance.refinement.included} used. Future refinements use the overage tier-pick flow.`,
            });
        }
        return out;
    }, [photos, treatmentDays, dayOfTray, cadence, isFinal, allowance.refinement]);

    return (
        <div className="space-y-5" data-testid="treatment-dashboard">
            {/* 1. Flags & alerts — top of the page, only when there's something to say. */}
            {flags.length > 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50/60 overflow-hidden" data-testid="treatment-flags">
                    <div className="px-5 py-3 border-b border-amber-200 bg-amber-50 flex items-center gap-2">
                        <Flag size={14} weight="fill" className="text-amber-700" />
                        <div className="text-[11px] font-bold uppercase tracking-wider text-amber-900">
                            Flags &amp; alerts
                        </div>
                        <span className="text-[10px] text-amber-700/70 tabular-nums ml-auto">
                            {flags.length} item{flags.length === 1 ? "" : "s"}
                        </span>
                    </div>
                    <div className="divide-y divide-amber-100">
                        {flags.map((f) => (
                            <div key={f.id} className="px-5 py-3 flex items-start gap-3" data-testid={`treatment-flag-${f.id}`}>
                                <WarningCircle
                                    size={16}
                                    weight="fill"
                                    className={f.tone === "red" ? "text-rose-600" : f.tone === "slate" ? "text-slate-500" : "text-amber-600"}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-foreground">{f.title}</div>
                                    <div className="text-[12px] text-muted-foreground mt-0.5">{f.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 2. Aligner progress — 4 metric cards in a responsive grid. */}
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="treatment-aligner-progress">
                <div className="flex items-center justify-between gap-3 mb-4">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                            Aligner progress
                        </div>
                        <h3 className="font-display text-lg font-bold mt-0.5">
                            Day {treatmentDays ?? "—"} of treatment {isFinal && <span className="text-emerald-700 text-sm font-semibold">· Final tray</span>}
                        </h3>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setStage(stage - 1)}
                            disabled={advancing || stage <= 1 || c?.is_locked}
                            className="rounded-full h-9 w-9"
                            data-testid="treatment-stage-prev"
                            title={c?.is_locked ? "Case is locked — clinical record frozen" : "Step back one aligner"}
                        >
                            <Minus size={13} weight="bold" />
                        </Button>
                        <Button
                            onClick={() => setStage(stage + 1)}
                            disabled={advancing || (total > 0 && stage >= total) || c?.is_locked}
                            className="rounded-full h-9 px-4 text-xs font-semibold"
                            data-testid="treatment-stage-next"
                            title={c?.is_locked ? "Case is locked — clinical record frozen" : undefined}
                        >
                            <Plus size={13} weight="bold" className="mr-1" />
                            Advance
                        </Button>
                    </div>
                </div>

                {/* Progress bar */}
                {total > 0 && (
                    <div className="space-y-1.5 mb-5">
                        <div className="h-2 bg-secondary rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground tabular-nums">
                            <span className="font-semibold">{pct}% complete</span>
                            <span>{Math.max(0, total - stage)} aligners to go</span>
                        </div>
                    </div>
                )}

                {/* 5 metric tiles — Estimated completion tile (iter93.1) joins the row */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    <Metric
                        label="Current aligner"
                        value={total ? `${stage} / ${total}` : `${stage}`}
                        sub={isFinal ? "Final tray" : `${Math.max(0, total - stage)} more to go`}
                        tone="primary"
                    />
                    <Metric
                        label="Day of tray"
                        value={dayOfTray != null ? `Day ${dayOfTray}` : "—"}
                        sub={`of ${cadence}-day cycle`}
                        tone={dayOfTray != null && dayOfTray > cadence ? "amber" : "default"}
                    />
                    <Metric
                        label="Next switch"
                        value={
                            nextSwitchInDays == null ? "—"
                                : nextSwitchInDays === 0 ? "Today"
                                    : nextSwitchInDays === 1 ? "Tomorrow"
                                        : `In ${nextSwitchInDays}d`
                        }
                        sub={stageAnchor ? `Anchored ${fmtDate(stageAnchor)}` : ""}
                        tone={nextSwitchInDays != null && nextSwitchInDays <= 1 ? "emerald" : "default"}
                    />
                    <Metric
                        label="Treatment started"
                        value={fittedAt ? fmtDate(fittedAt) : "—"}
                        sub={c.fitted_by_name ? `by ${c.fitted_by_name}` : ""}
                    />
                    <Metric
                        label="Estimated completion"
                        value={completion?.label || "—"}
                        sub={completion?.sub || "Needs fit + plan"}
                        tone={completion && completion.daysRemaining != null && completion.daysRemaining <= 30 ? "emerald" : "default"}
                    />
                </div>
            </div>

            {/* 3. Quick actions — 6-button strip. */}
            <div className="rounded-2xl border border-border bg-card p-4" data-testid="treatment-quick-actions">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2.5">
                    Quick actions
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                    <ActionTile
                        icon={Plus}
                        label="Advance aligner"
                        sub={isFinal ? "Final tray" : `Stage ${stage + 1}`}
                        onClick={() => setStage(stage + 1)}
                        disabled={advancing || isFinal || c?.is_locked}
                        testid="action-advance"
                    />
                    <ActionTile
                        icon={CalendarIcon}
                        label="Log visit"
                        sub="In-person check-in"
                        onClick={() => toast.info("Visit logging arrives in iter93.2 — for now, note in chat.")}
                        testid="action-log-visit"
                    />
                    <ActionTile
                        icon={ArrowsCounterClockwise}
                        label="Request refinement"
                        sub={allowance.refinement
                            ? (allowance.refinement.remaining > 0
                                ? `${allowance.refinement.remaining} included`
                                : "Overage rates")
                            : ""}
                        onClick={() => spawnChild("refinement")}
                        disabled={spawnBusy === "refinement"}
                        tone="amber"
                        testid="action-request-refinement"
                    />
                    <ActionTile
                        icon={Crown}
                        label="Order retainer"
                        sub={allowance.retainer
                            ? (allowance.retainer.remaining > 0
                                ? `${allowance.retainer.remaining} included`
                                : "Overage rates")
                            : ""}
                        onClick={() => spawnChild("retainer")}
                        disabled={spawnBusy === "retainer"}
                        tone="violet"
                        testid="action-order-retainer"
                    />
                    <ActionTile
                        icon={FilePdf}
                        label="Care card"
                        sub="Patient PDF"
                        onClick={() => setCareCardOpen(true)}
                        testid="action-care-card"
                    />
                    <ActionTile
                        icon={Flag}
                        label="Flag issue"
                        sub="Open chat"
                        onClick={onFlagIssue}
                        tone="rose"
                        testid="action-flag-issue"
                    />
                </div>
            </div>

            {/* 4. Progress photos — 5-week strip + Compare. */}
            <PhotoStrip caseId={c.id} photos={photos} fittedAt={fittedAt} />

            {/* 5. Visit log + Compliance side by side. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <VisitLog caseData={c} treatmentDays={treatmentDays} />
                <ComplianceCard caseData={c} photos={photos} treatmentDays={treatmentDays} cadence={cadence} />
            </div>

            <CareCardDialog
                open={careCardOpen}
                onOpenChange={setCareCardOpen}
                caseId={c.id}
                patientName={c.patient_name}
            />
        </div>
    );
}


/* ─── Section components ──────────────────────────────────────────────── */

function Metric({ label, value, sub, tone = "default" }) {
    const toneClasses = {
        default: "bg-secondary/40 border-transparent",
        primary: "bg-primary/5 border-primary/15",
        amber: "bg-amber-50 border-amber-200",
        emerald: "bg-emerald-50 border-emerald-200",
    }[tone] || "bg-secondary/40 border-transparent";
    return (
        <div className={`rounded-xl border p-3 ${toneClasses}`} data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
            <div className="font-display text-xl font-bold tabular-nums leading-tight mt-1">{value}</div>
            {!!sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
        </div>
    );
}


function ActionTile({ icon: Icon, label, sub, onClick, disabled, tone = "default", testid }) {
    const toneClasses = {
        default: "border-border hover:border-primary/40 hover:bg-secondary/30",
        amber: "border-amber-200 bg-amber-50/40 hover:border-amber-300",
        violet: "border-violet-200 bg-violet-50/40 hover:border-violet-300",
        rose: "border-rose-200 bg-rose-50/40 hover:border-rose-300",
    }[tone] || "";
    const iconClasses = {
        default: "text-primary",
        amber: "text-amber-600",
        violet: "text-violet-600",
        rose: "text-rose-600",
    }[tone] || "text-primary";
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className={`rounded-xl border p-3 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${toneClasses}`}
        >
            <div className="flex items-center justify-between mb-1.5">
                <Icon size={16} weight="duotone" className={iconClasses} />
            </div>
            <div className="text-[12px] font-semibold leading-tight">{label}</div>
            {!!sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
        </button>
    );
}


function PhotoStrip({ caseId, photos, fittedAt }) {
    const [open, setOpen] = useState(null);
    const [compareOpen, setCompareOpen] = useState(false);

    // Bucket the photos by week-since-fitted. Show the LAST 5 weeks
    // (the doctor cares about recent compliance, not the start).
    const weeks = useMemo(() => {
        if (!photos || !fittedAt) return [];
        const photosWithWeek = photos.map((p) => {
            const days = daysBetween(fittedAt, p.uploaded_at);
            return { ...p, week: Math.floor(days / 7) + 1 };
        });
        const totalWeeks = Math.max(1, Math.ceil(daysBetween(fittedAt) / 7) + 1);
        const startWeek = Math.max(1, totalWeeks - 4);
        const buckets = [];
        for (let w = startWeek; w <= startWeek + 4; w++) {
            const inWeek = photosWithWeek.filter((p) => p.week === w);
            buckets.push({ week: w, photos: inWeek });
        }
        return buckets;
    }, [photos, fittedAt]);

    if (photos === null) return null;

    const hasMultiple = (photos || []).length >= 2;

    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="treatment-photo-strip">
            <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center gap-2">
                <Camera size={14} weight="duotone" className="text-primary" />
                <div className="font-display font-semibold text-sm">Progress photos</div>
                <span className="text-[10px] text-muted-foreground ml-2 tabular-nums">{photos.length} uploaded</span>
                <Button
                    variant="outline"
                    size="sm"
                    className="ml-auto h-8 rounded-full text-xs"
                    disabled={!hasMultiple}
                    onClick={() => setCompareOpen(true)}
                    data-testid="photo-compare-btn"
                >
                    <ArrowsLeftRight size={11} className="mr-1" /> Compare before / now
                </Button>
            </div>
            {photos.length === 0 ? (
                <div className="px-5 py-6 text-center text-xs text-muted-foreground">
                    Patient hasn't uploaded any photos yet. Nudge them via WhatsApp from the chat tab.
                </div>
            ) : (
                <div className="p-4 grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {weeks.map(({ week, photos: ws }) => (
                        <button
                            key={week}
                            type="button"
                            disabled={ws.length === 0}
                            onClick={() => ws[0] && setOpen(ws[0])}
                            className={`aspect-square rounded-lg border overflow-hidden relative group ${
                                ws.length === 0
                                    ? "border-dashed border-border bg-secondary/30"
                                    : "border-border"
                            }`}
                            data-testid={`photo-week-${week}`}
                        >
                            {ws.length > 0 ? (
                                <>
                                    <img src={ws[0].data_url} alt={`Week ${week}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                    {/* iter98.10 — flag the tile when ANY photo in this
                                        week came in via the structured Weekly check-in
                                        card (carries feels_good signal). Gallery-only
                                        weeks stay un-chipped to keep the grid quiet. */}
                                    {ws.some((p) => p.source === "check_in") && (
                                        <span
                                            className="absolute top-1 right-1 px-1.5 py-0.5 rounded-full bg-emerald-500/95 text-white text-[9px] font-bold uppercase tracking-wider shadow-sm"
                                            data-testid={`photo-week-${week}-checkin-chip`}
                                        >
                                            Check-in
                                        </span>
                                    )}
                                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1 text-white text-[10px] font-semibold">
                                        Week {week}{ws.length > 1 ? ` · ${ws.length}` : ""}
                                    </div>
                                </>
                            ) : (
                                <div className="w-full h-full grid place-items-center text-[10px] text-muted-foreground">
                                    Week {week} <br /> missing
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            )}

            {/* Full-screen viewer */}
            {open && (
                <div className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4" onClick={() => setOpen(null)}>
                    <button
                        onClick={() => setOpen(null)}
                        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center"
                    ><X size={18} weight="bold" /></button>
                    <div className="max-w-3xl" onClick={(e) => e.stopPropagation()}>
                        <img src={open.data_url} alt={open.note || "Photo"} className="max-w-full max-h-[80vh] object-contain rounded-xl" />
                        {/* iter98.10 — source provenance + check-in mood
                            signal. Patients flag "feels good?" yes/no on
                            the weekly card; surfacing it here tells the
                            doctor whether the photo lines up with how
                            the patient is feeling. */}
                        <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
                            {open.source === "check_in" ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 text-[10px] font-bold uppercase tracking-wider border border-emerald-400/40">
                                    Weekly check-in{open.week_of ? ` · ${open.week_of}` : ""}
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-white/80 text-[10px] font-bold uppercase tracking-wider border border-white/15">
                                    Gallery upload
                                </span>
                            )}
                            {open.source === "check_in" && open.feels_good === true && (
                                <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 text-[10px] font-semibold border border-emerald-400/40">
                                    Patient: feels good
                                </span>
                            )}
                            {open.source === "check_in" && open.feels_good === false && (
                                <span className="px-2 py-0.5 rounded-full bg-amber-500/25 text-amber-200 text-[10px] font-semibold border border-amber-400/50">
                                    Patient: not feeling great
                                </span>
                            )}
                        </div>
                        {open.note && <div className="mt-3 text-white/90 text-center text-sm">{open.note}</div>}
                        <div className="mt-1 text-white/60 text-center text-xs">{new Date(open.uploaded_at).toLocaleString("en-IN")}</div>
                    </div>
                </div>
            )}

            {/* Compare overlay — split-screen first vs latest photo */}
            {compareOpen && photos && photos.length >= 2 && (() => {
                const sorted = [...photos].sort((a, b) => new Date(a.uploaded_at) - new Date(b.uploaded_at));
                const before = sorted[0];
                const after = sorted[sorted.length - 1];
                return (
                    <div className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4" onClick={() => setCompareOpen(false)} data-testid="photo-compare-overlay">
                        <button
                            onClick={() => setCompareOpen(false)}
                            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center"
                        ><X size={18} weight="bold" /></button>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl w-full">
                            <ComparePane label="Before" subtitle={fmtDateLong(before.uploaded_at)} photo={before} />
                            <ComparePane label="Now" subtitle={fmtDateLong(after.uploaded_at)} photo={after} />
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}


function ComparePane({ label, subtitle, photo }) {
    return (
        <div onClick={(e) => e.stopPropagation()}>
            <div className="text-white/80 text-[10px] font-bold uppercase tracking-[0.18em] mb-1">{label}</div>
            <div className="text-white text-base font-display font-bold mb-2">{subtitle}</div>
            <img src={photo.data_url} alt={label} className="w-full max-h-[70vh] object-contain rounded-xl" />
            {photo.note && <div className="mt-2 text-white/80 text-xs">{photo.note}</div>}
        </div>
    );
}


function VisitLog({ caseData, treatmentDays }) {
    /* Derived from case lifecycle fields. We don't have a dedicated visit
       log table yet — when one lands (iter93.2), swap the entries
       array for a live fetch. */
    const entries = useMemo(() => {
        const items = [];
        if (caseData.submitted_at) {
            items.push({
                date: caseData.submitted_at,
                label: "Case submitted",
                desc: "Doctor's prescription sent to planner",
                tone: "default",
            });
        }
        if (caseData.fitted_at) {
            items.push({
                date: caseData.fitted_at,
                label: "Aligners fitted",
                desc: caseData.fitted_by_name ? `Logged by ${caseData.fitted_by_name}` : "First aligner seated",
                tone: "emerald",
            });
        }
        if (caseData.aligner_stage_updated_at && caseData.aligner_stage_updated_at !== caseData.fitted_at) {
            items.push({
                date: caseData.aligner_stage_updated_at,
                label: `Stage advanced to ${caseData.current_aligner_stage}`,
                desc: "Tray switch noted",
                tone: "default",
            });
        }
        // Upcoming — derive a "mid-treatment review" hint at day ~60.
        if (caseData.fitted_at && treatmentDays != null && treatmentDays < 60) {
            const reviewDate = addDays(caseData.fitted_at, 60);
            items.push({
                date: reviewDate,
                label: "Mid-treatment review",
                desc: `Upcoming · in ${60 - treatmentDays} days`,
                tone: "upcoming",
            });
        }
        return items.sort((a, b) => new Date(a.date) - new Date(b.date));
    }, [caseData, treatmentDays]);

    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="treatment-visit-log">
            <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center gap-2">
                <ClipboardText size={14} weight="duotone" className="text-primary" />
                <div className="font-display font-semibold text-sm">Visit log</div>
                <span className="text-[10px] text-muted-foreground ml-2 tabular-nums">{entries.length} events</span>
            </div>
            <ul className="px-5 py-3 space-y-3">
                {entries.map((e, i) => (
                    <li key={i} className="flex gap-3" data-testid={`visit-log-${i}`}>
                        <div className={`w-7 h-7 rounded-full shrink-0 grid place-items-center text-[10px] font-bold ${
                            e.tone === "emerald" ? "bg-emerald-100 text-emerald-700"
                                : e.tone === "upcoming" ? "bg-amber-50 text-amber-700 border border-dashed border-amber-300"
                                    : "bg-secondary text-foreground"
                        }`}>
                            {e.tone === "upcoming" ? "•" : i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold leading-tight">{e.label}</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                                {fmtDateLong(e.date)} {e.desc ? `· ${e.desc}` : ""}
                            </div>
                        </div>
                    </li>
                ))}
                {entries.length === 0 && (
                    <li className="text-xs text-muted-foreground text-center py-2">No visits logged yet.</li>
                )}
            </ul>
        </div>
    );
}


function ComplianceCard({ caseData, photos, treatmentDays, cadence }) {
    /* Derived compliance read — combines photo upload rate + stage advance
       cadence into a single 0-100 score plus a short narrative. */
    const score = useMemo(() => {
        if (!treatmentDays || treatmentDays < 7) return { value: null, narrative: "Too early to tell." };
        // Photo signal: 1 photo per 14 days = 100%, less → linearly lower
        const photoCount = photos?.length || 0;
        const photoWeeks = Math.max(1, Math.floor(treatmentDays / 7));
        const photoRatio = Math.min(1, photoCount / Math.max(1, photoWeeks / 2));
        // Stage signal: stage / expected stage (treatment_days / cadence)
        const stage = Number(caseData.current_aligner_stage || 0);
        const expectedStage = Math.max(1, Math.floor(treatmentDays / cadence));
        const stageRatio = expectedStage > 0 ? Math.min(1.1, stage / expectedStage) : 0.5;
        // Combine — both signals weighted equally
        const composite = Math.round((photoRatio * 0.5 + Math.min(1, stageRatio) * 0.5) * 100);

        let narrative;
        if (composite >= 80) {
            narrative = "Excellent — patient is on track with photos and tray switches.";
        } else if (composite >= 60) {
            narrative = "Healthy — minor delays but treatment is progressing.";
        } else if (composite >= 40) {
            narrative = "Watch closely — gaps in either photos or tray cadence.";
        } else {
            narrative = "Patient engagement is low. Consider reaching out.";
        }
        return { value: composite, narrative, photoCount, photoWeeks, stage, expectedStage };
    }, [treatmentDays, photos, caseData.current_aligner_stage, cadence]);

    const tone = !score.value ? "slate"
        : score.value >= 80 ? "emerald"
            : score.value >= 60 ? "primary"
                : score.value >= 40 ? "amber"
                    : "rose";
    const fillClass = {
        emerald: "bg-emerald-500",
        primary: "bg-primary",
        amber: "bg-amber-500",
        rose: "bg-rose-500",
        slate: "bg-slate-400",
    }[tone];

    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="treatment-compliance">
            <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center gap-2">
                <Heart size={14} weight="duotone" className="text-primary" />
                <div className="font-display font-semibold text-sm">Compliance</div>
            </div>
            <div className="p-5 space-y-3">
                {score.value == null ? (
                    <div className="text-sm text-muted-foreground text-center py-4">
                        {score.narrative}
                    </div>
                ) : (
                    <>
                        <div className="flex items-baseline justify-between">
                            <div>
                                <div className="font-display text-4xl font-bold tabular-nums" data-testid="compliance-score">{score.value}</div>
                                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
                                    out of 100
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[11px] text-muted-foreground">Photos uploaded</div>
                                <div className="font-semibold text-sm tabular-nums">{score.photoCount} <span className="text-muted-foreground font-normal">/ {score.photoWeeks}+</span></div>
                            </div>
                        </div>
                        <div className="h-2 bg-secondary rounded-full overflow-hidden">
                            <div className={`h-full transition-all ${fillClass}`} style={{ width: `${score.value}%` }} />
                        </div>
                        <p className="text-[12px] text-muted-foreground leading-snug">{score.narrative}</p>
                        <div className="grid grid-cols-2 gap-2 text-[11px] pt-1 border-t border-border">
                            <div>
                                <div className="text-muted-foreground">Tray cadence</div>
                                <div className="font-semibold tabular-nums">
                                    {score.stage} / {score.expectedStage} expected
                                </div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Photo cadence</div>
                                <div className="font-semibold tabular-nums">
                                    {score.photoCount}/{score.photoWeeks} weekly
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
